import React from 'react';
import parse, { domToReact } from 'html-react-parser';
import { TEXT_TYPE } from '../../constants';
import TiptapCodeEditor from '../assignments/AssignmentQuestion/tiptap-editor';
import { renderKatex } from '../../utils/domUtils';
import { getPdfFileUrl, PdfViewer } from './PdfViewer';
// import 'suneditor/dist/css/suneditor.min.css';
// import 'katex/dist/katex.min.css';

const getYoutubeEmbedUrl = (src) => {
  if (!src) return null;

  try {
    const url = new URL(src, 'https://example.com');

    if (url.hostname === 'youtu.be') {
      const videoId = url.pathname.replace(/^\//, '').split('/')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    if (url.hostname.includes('youtube.com') || url.hostname.includes('youtube-nocookie.com')) {
      if (url.pathname.includes('/embed/')) {
        return src;
      }

      const videoId = url.searchParams.get('v');
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }

      const pathMatch = url.pathname.match(/\/(shorts|live)\/([^/?]+)/);
      if (pathMatch?.[2]) {
        return `https://www.youtube.com/embed/${pathMatch[2]}`;
      }
    }
  } catch {
    return src;
  }

  return src;
};

const renderWelcomeVideo = ({ src, title, allow, frameborder }) => {
  const embedSrc = getYoutubeEmbedUrl(src);

  if (!embedSrc) {
    return null;
  }

  return (
    <div className="welcome-video-wrapper">
      <iframe
        src={embedSrc}
        title={title || 'Embedded video'}
        allow={
          allow ||
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
        }
        allowFullScreen
        frameBorder={frameborder || '0'}
      />
    </div>
  );
};

const renderPdfViewer = (src) => (
  <PdfViewer fileUrl={src} variant="light" className="my-3 w-full" />
);

const getParserOptions = (variant) => ({
  replace: (domNode) => {
    if (!domNode) return;

    const katexRendered = renderKatex(domNode);
    if (katexRendered !== undefined) {
      return katexRendered;
    }

    if (domNode.name === 'iframe' || domNode.name === 'embed' || domNode.name === 'object') {
      const src = domNode.attribs?.src || domNode.attribs?.data;
      const pdfUrl = getPdfFileUrl(src, domNode.attribs?.type);
      if (pdfUrl) {
        return renderPdfViewer(pdfUrl);
      }
    }

    if (variant === 'welcome' && domNode.name === 'iframe') {
      const { src, title, allow, frameborder } = domNode.attribs || {};
      return renderWelcomeVideo({ src, title, allow, frameborder });
    }

    if (variant === 'welcome' && domNode.name === 'a') {
      const { href, title } = domNode.attribs || {};
      const embedSrc = getYoutubeEmbedUrl(href);

      if (embedSrc?.includes('/embed/')) {
        return renderWelcomeVideo({ src: embedSrc, title });
      }
    }

    if (variant === 'welcome' && domNode.name === 'embed') {
      const { src, title } = domNode.attribs || {};
      return renderWelcomeVideo({ src, title });
    }

    if (variant === 'welcome' && domNode.name === 'img') {
      const { src, alt, title, width, height } = domNode.attribs || {};

      return (
        <img
          src={src}
          alt={alt || title || 'instruction image'}
          width={width || undefined}
          height={height || undefined}
          className="welcome-content-image"
          loading="lazy"
        />
      );
    }

    if (variant === 'welcome' && domNode.name === 'div' && domNode.attribs?.style) {
      const style = domNode.attribs.style.toLowerCase();
      let calloutClass = '';

      if (
        style.includes('eff6ff') ||
        style.includes('239, 246, 255') ||
        style.includes('dbeafe') ||
        style.includes('219, 234, 254')
      ) {
        calloutClass = 'welcome-html-block welcome-callout-blue';
      } else if (
        style.includes('fffbeb') ||
        style.includes('255, 251, 235') ||
        style.includes('fde68a') ||
        style.includes('253, 230, 138')
      ) {
        calloutClass = 'welcome-html-block welcome-callout-amber';
      } else if (
        style.includes('f0fdf4') ||
        style.includes('240, 253, 244') ||
        style.includes('bbf7d0') ||
        style.includes('187, 247, 208')
      ) {
        calloutClass = 'welcome-html-block welcome-callout-green';
      }

      if (calloutClass) {
        return (
          <div className={calloutClass}>
            {domToReact(domNode.children, getParserOptions(variant))}
          </div>
        );
      }
    }
  }
});

const HTMLRenderer = (props) => {
  const {
    element: { html, type },
    textType,
    variant
  } = props;
  if (!html) {
    return null;
  }
  if (type === 'image') {
    if (variant === 'welcome') {
      return (
        <img
          src={html}
          alt="instruction image"
          className="welcome-content-image"
          loading="lazy"
        />
      );
    }

    return <img src={html} alt="image" className="max-w-full h-auto" />;
  }
  if (type === 'pdf' || type === 'file' || type === 'document') {
    const pdfUrl = getPdfFileUrl(html);
    if (pdfUrl) {
      return <PdfViewer fileUrl={pdfUrl} variant="light" className="my-3 w-full" />;
    }
  }
  if (type === 'table') {
    const tableClassName =
      variant === 'welcome' ? 'sun-editor-editable welcome-html-block' : 'sun-editor-editable';

    return <div className={tableClassName}>{parse(html, getParserOptions(variant))}</div>;
  }

  // Return a non editable tiptap textbox
  if (textType === TEXT_TYPE.TIPTAP) {
    return <TiptapCodeEditor value={html} disabled />;
  }

  const htmlClassName = variant === 'welcome' ? 'html-renderer welcome-html-block' : 'html-renderer';
  const HtmlWrapper = variant === 'welcome' ? 'div' : 'span';

  return (
    <HtmlWrapper className={htmlClassName}>{parse(html, getParserOptions(variant))}</HtmlWrapper>
  );
};

export { HTMLRenderer };
